import {useQuery} from '@tanstack/react-query'
import {Play, Loader2} from 'lucide-react'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Button} from '@/components/ui/button'
import {SqlEditor} from './SqlEditor'
import type {SqlSchema} from './types'

interface SqlEditorPanelProps {
    sql: string
    onChange: (sql: string) => void
    onRun?: () => void
    isRunning?: boolean
    readOnly?: boolean
    minHeight?: string
    maxHeight?: string
    showRunButton?: boolean
}

export function SqlEditorPanel({sql, onChange, onRun, isRunning, readOnly, minHeight, maxHeight, showRunButton = true}: SqlEditorPanelProps) {
    const i18n = useI18n()
    const services = useServices()

    const {data: schema} = useQuery<SqlSchema>({
        queryKey: ['sql-schema'],
        queryFn: () => services.reporting.sqlSchema(),
    })

    return (
        <div className="space-y-2">
            {(!readOnly && showRunButton && onRun) && (
                <div className="flex items-center justify-end">
                    <Button
                        data-testid="btn-run-query"
                        variant="default"
                        size="sm"
                        onClick={onRun}
                        disabled={isRunning || !sql.trim()}
                    >
                        {isRunning
                            ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin"/>{i18n.reporting.running}</>
                            : <><Play className="h-3.5 w-3.5 mr-1"/>{i18n.reporting.trySql}</>
                        }
                        {/* eslint-disable-next-line i18next/no-literal-string */}
                        {!isRunning && <span className="ml-1.5 text-xs opacity-60">{'\u2318'}+Enter</span>}
                    </Button>
                </div>
            )}
            <SqlEditor
                sql={sql}
                onChange={onChange}
                onRun={onRun}
                readOnly={readOnly}
                schema={schema}
                minHeight={minHeight}
                maxHeight={maxHeight}
            />
        </div>
    )
}
